import React, { FC } from 'react';

import { Box, Typography } from '@mui/material';

import {
  IAdminRequestDetailsContractModel,
  ILongContractRequestResponse,
} from '@pharmaplan/common';

import useGetLongTermDistributions from '../../../../hooks/PharmacistProfile/useGetLongTermDistributions';
import HourlyRateChipLabel from '../../../common/AdminHourlyRateFooter/HourlyRateChipLabel';
import LongShiftDistributionModal from '../LongShiftDistributionModal';
import { ChipType, Constants } from '../../../../helpers/Constants';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { setDialog } from '../../../../reducers/dialogReducer';
import CustomButton from '../../../common/CustomButton';
import strings from '../../../../localization';

import ShiftsDistributionItem from './ShiftsDistributionItem';
import styles from './styles';

const { hourlyRate } = ChipType;

interface ILongShiftsDistribution {
  weekdayHourlyRate: number;
  weekendHourlyRate: number;
  counterWeekdayRate?: number;
  counterWeekendRate?: number;
  successAction: string;
  details: ILongContractRequestResponse | IAdminRequestDetailsContractModel;
}

const LongShiftsDistribution: FC<ILongShiftsDistribution> = ({
  details,
  weekdayHourlyRate,
  weekendHourlyRate,
  counterWeekdayRate,
  counterWeekendRate,
  successAction,
}) => {
  const dispatch = useAppDispatch();
  const { requests, counterRequests } = details ?? {};
  const { professionalName } = details as IAdminRequestDetailsContractModel;

  const { totalShifts } = useGetLongTermDistributions(
    requests,
    counterRequests,
    3,
  );

  const openModal = () => {
    dispatch(
      setDialog({
        Component: (
          <LongShiftDistributionModal
            successAction={successAction}
            name={professionalName}
            details={details}
          />
        ),
        heading: {
          title: `${strings.shiftDetails} (${strings.formatString(
            strings.numberRequests,
            requests?.length,
          )})`,
        },
        showCloseButton: true,
        maxWidth: 'lg',
      }),
    );
  };

  const showCounterWeekday = counterWeekdayRate && counterWeekdayRate !== weekdayHourlyRate;
  const showCounterWeekend = counterWeekendRate && counterWeekendRate !== weekendHourlyRate;

  const showWeekday = !!(counterWeekdayRate || weekdayHourlyRate);
  const showWeekend = !!(counterWeekendRate || weekendHourlyRate);

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>
        {`${strings.shiftDetails} (${strings.formatString(
          requests?.length > 1 ? strings.numberRequests : strings.numberRequest,
          requests?.length,
        )})`}
      </Typography>

      <Box sx={styles.rateContainer}>
        {showWeekday && (
          <HourlyRateChipLabel
            type={hourlyRate}
            label={strings.weekdayHourlyRate}
            rate={counterWeekdayRate ?? weekdayHourlyRate}
            successAction={successAction}
            strikeRate={showCounterWeekday ? weekdayHourlyRate : null}
          />
        )}

        {showWeekend && (
          <HourlyRateChipLabel
            type={hourlyRate}
            label={strings.weekendHourlyRate}
            rate={counterWeekendRate ?? weekendHourlyRate}
            successAction={successAction}
            strikeRate={showCounterWeekend ? weekendHourlyRate : null}
          />
        )}
      </Box>

      <Box sx={styles.datesRange}>
        <ShiftsDistributionItem totalShifts={totalShifts} />
      </Box>

      {true && (
        <CustomButton
          variant={Constants.variant.outlined}
          label={strings.viewMore}
          onClick={openModal}
        />
      )}
    </Box>
  );
};

export default LongShiftsDistribution;
